<template>
  <div
    :style="rootStyle"
    :title="item.name"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    draggable="true"
    class="pui-file-gallery-item"
  >
    <pui-flex
      :class="paddingContainerClass"
      direction="column"
      justifyContent="start"
      alignItems="center"
    >
      <pui-card
        :class="cardClass"
        :style="cardStyle"
        :bottomZoneClass="bottomZoneClass"
        :topZoneClass="topZoneClass"
        @clickTop="$emit('checkChange', !checked, $event)"
        @clickBottom="$emit('clickInfo', $event)"
        enableBottomZone
        enableTopZone
      >
        <template #default>
          <div :class="{ 'file-gallery-item-card-content': true, loading: hasTasksInProgress }">
            <pui-flex
              :class="imageCheckedContainerClass"
              justifyContent="center"
              alignItems="center"
            >
              <pui-file-gallery-item-thumbnail
                :file="item"
                :objectFit="objectFit"
                :itemColIndex="itemColIndex"
                :supportsScrollingDetection="supportsScrollingDetection"
                :isParentScrolling="isParentScrolling"
              />

              <div class="file-gallery-item-card__file-indicator-container">
                <i
                  v-if="showVideoIndicator"
                  class="file-gallery-item-card__file-indicator-item circle-bg-white video-file mdi mdi-play"
                />
              </div>

              <div
                v-if="borderStyle"
                :style="borderStyle"
                class="file-gallery-item-thumbnail-border"
              ></div>
            </pui-flex>
          </div>
        </template>

        <template #topLeft>
          <pui-checkbox :class="checkboxClass" :checked="checked" @change="onCheckboxChange" />
        </template>

        <template #bottomLeft>
          <i :class="infoIconClass" />
        </template>

        <template #bottomRight>
          <i
            :class="zoomIconClass"
            @click.stop="$emit('clickZoom', $event)"
            @mouseenter="onHoverZoom(true)"
            @mouseleave="onHoverZoom(false)"
          />
        </template>

        <template v-if="hasTasksInProgress" #center>
          <!-- This svg corresponds to mdi-timer-sand -->
          <!-- We need to use the svg instead of the font because we want -->
          <!-- to rotate it around its center (font messes up the center) -->
          <svg viewBox="0 0 24 24" class="has-tasks-in-progress-icon">
            <path
              d="M6,2H18V8H18V8L14,12L18,16V16H18V22H6V16H6V16L10,12L6,8V8H6V2M16,16.5L12,12.5L8,16.5V20H16V16.5M12,11.5L16,7.5V4H8V7.5L12,11.5M10,6H14V6.75L12,8.75L10,6.75V6Z"
            />
          </svg>
        </template>
      </pui-card>
      <pui-flex
        v-if="showMeta"
        :style="metaStyle"
        class="pui-file-gallery-item__meta"
        justifyContent="center"
        alignItems="center"
        flex="1"
      >
        <span class="pui-file-gallery-item__name">{{ item.name }}</span>
      </pui-flex>
    </pui-flex>
  </div>
</template>

<script>
import { Mode as THUMBNAIL_VIEWER_MODE } from 'piivo-ui/src/components/image/ImageViewer';

import {
  FILE_GALLERY_ITEM_META_HEIGHT_PX,
  FileDescriptionTypes,
  FileTaskStatus,
} from '../../constants/file';
import { getFileDynamicBorderColor } from '../../helpers/fileHelper';
import puiFileGalleryItemThumbnail, { ObjectFit } from './FileGalleryItemThumbnail';
import { getDamService } from '../../services';

/**
 * Class to apply to all actions
 */
const ACTION_CLASS = 'file-gallery-item-action';

export default {
  name: 'pui-file-gallery-item',
  components: {
    puiFileGalleryItemThumbnail,
  },
  inheritAttrs: false, // Prevent unknown props making us re render
  props: {
    /**
     * Height for the root element
     *
     * @type {Number}
     */
    height: {
      type: Number,
    },
    /**
     * Object fit mode for the real image
     *
     * @type {String}
     * @default "cover"
     */
    objectFit: {
      type: String,
      default: ObjectFit.COVER,
      validator: value => Object.values(ObjectFit).includes(value),
    },
    /**
     * Width for the root element, either a number in px
     * or the width of its parent (doesn't need to be applied)
     *
     * @type {Number}
     */
    width: {
      type: [Number, String],
    },
    /**
     * Margin for the root element
     *
     * @type {Number}
     */
    margin: {
      type: Number,
      default: 0,
    },
    /**
     * Asset object
     *
     * @type {Object}
     */
    item: {
      type: Object,
      required: true,
    },
    /**
     * The index of this item in the array of items
     */
    itemIndex: {
      type: Number,
      required: true,
    },
    /**
     * The column at which this is being rendered
     */
    itemColIndex: {
      type: Number,
      required: true,
    },
    /**
     * State of checkbox.
     * Will make the checkbox and deselect icon always
     * visible if true
     *
     * @type {Boolean}
     * @default false
     */
    checked: {
      type: Boolean,
      default: false,
    },
    /**
     * If this item's info is being displayed somewhere.
     * Will make the info icon always visible
     *
     * @type {Boolean}
     * @default false
     */
    isShowingInfo: {
      type: Boolean,
      default: false,
    },
    /**
     * If this item's image is being zommed in somewhere.
     * Will make the zoom icon always visible
     *
     * @type {Boolean}
     * @default false
     */
    isZoomed: {
      type: Boolean,
      default: false,
    },
    /**
     * If this item is being dragged
     *
     * @type {Boolean}
     * @default false
     */
    isBeingDragged: {
      type: Boolean,
      default: false,
    },
    /**
     * If should apply the dimensions
     * received via props
     *
     * @type {Boolean}
     * @default false
     */
    applyPropDimensions: {
      type: Boolean,
      default: false,
    },
    /**
     * If can use the 'isParentScrolling' prop to check
     * if the parent is scrolling.
     */
    supportsScrollingDetection: {
      type: Boolean,
      default: false,
    },
    /**
     * If the parent is scrolling
     */
    isParentScrolling: {
      type: Boolean,
      default: false,
    },
    /**
     * If should display meta
     */
    showMeta: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      THUMBNAIL_VIEWER_MODE,
      isHoveringZoom: false,
    };
  },
  computed: {
    /**
     * Style for the root element to set its dimensions
     *
     * @returns {Object}
     */
    rootStyle() {
      if (this.applyPropDimensions) {
        return {
          width: `${this.width}px`,
          height: `${this.height}px`,
          minHeight: `${this.height}px`,
          maxHeight: `${this.height}px`,
          margin: `${0}px`, // TODO: how to handle margins
        };
      }

      return {};
    },
    /**
     * @returns {object} style object for the meta
     */
    metaStyle() {
      return {
        height: `${FILE_GALLERY_ITEM_META_HEIGHT_PX}px`,
      };
    },
    imageCheckedContainerClass() {
      return {
        'file-gallery-item-thumbnail-viewer-container': true,
        checked: this.checked,
      };
    },
    /**
     * Class object for the padding container
     *
     * @returns {Object}
     */
    paddingContainerClass() {
      return {
        'file-gallery-item-padding-container': true,
        checked: this.checked,
        isShowingInfo: this.isShowingInfo,
      };
    },
    cardClass() {
      return {
        'file-gallery-item-card': true,
        isBeingDragged: this.isBeingDragged,
        expandHeight: !this.showMeta,
      };
    },
    /**
     * @returns {object} style object for the card
     */
    cardStyle() {
      if (this.showMeta) {
        return {
          height: `calc(100% - ${FILE_GALLERY_ITEM_META_HEIGHT_PX}px)`,
        };
      }

      return {};
    },
    /**
     * @returns {object} class object for the bottom zone
     */
    bottomZoneClass() {
      return {
        isHoveringZoom: this.isHoveringZoom,
      };
    },
    /**
     * @returns {object} class object for the top zone
     */
    topZoneClass() {
      return {};
    },
    /**
     * Class object for the checkbox container
     *
     * @returns {Object}
     */
    checkboxClass() {
      return {
        [ACTION_CLASS]: true,
        'action-checkbox': true,
        checked: this.checked,
      };
    },
    /**
     * Class object for the info icon
     *
     * @returns {Object}
     */
    infoIconClass() {
      return {
        [ACTION_CLASS]: true,
        'action-info mdi mdi-information': true,
        isShowingInfo: this.isShowingInfo,
      };
    },
    /**
     * Class object for the zoom icon
     *
     * @returns {Object}
     */
    zoomIconClass() {
      return {
        [ACTION_CLASS]: true,
        'action-zoom mdi mdi-magnify-plus': true,
        isZoomed: this.isZoomed,
      };
    },
    /**
     * @returns {boolean} if should display the video indicator
     */
    showVideoIndicator() {
      return (
        this.item &&
        this.item.description &&
        this.item.description.type === FileDescriptionTypes.VIDEO
      );
    },
    /**
     * @returns {string} the border color to apply
     */
    borderStyle() {
      const borderColor = getFileDynamicBorderColor(this.item.attributes);
      if (borderColor) {
        return {
          borderColor,
        };
      }

      return null;
    },
    /**
     * @returns {boolean} if any of the file's tasks are in progress
     */
    hasTasksInProgress() {
      return getDamService('fileTasks')
        .getCurrentFilesTasks([this.item.itemId])
        .some(t => t.data.state() === FileTaskStatus.IN_PROGRESS);
    },
  },
  methods: {
    /**
     * Emits the 'dragStart' event
     *
     * @param {DropEvent} e - the drag event
     * @returns {void}
     */
    onDragStart(e) {
      this.$emit('dragStart', e);
    },
    /**
     * Emits the 'dragEnd' event
     *
     * @param {DropEvent} e - the drag event
     * @returns {void}
     */
    onDragEnd(e) {
      this.$emit('dragEnd');
    },
    /**
     * Updates the zoom hover value
     *
     * @param {boolean} value - if the zoom is being hovered
     * @returns {void}
     */
    onHoverZoom(value) {
      this.isHoveringZoom = value;
    },
    /**
     * Handles a checkbox change
     *
     * @param {boolean} checked - the new check state
     * @param {MouseEvent} event - the click event
     */
    onCheckboxChange(checked, event) {
      this.$emit('checkChange', checked, event);
    },
  },
};
</script>
